import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
// @ts-expect-error - subpath export; moduleResolution node
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import {
  Box,
  FormHelperText,
  IconButton,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import TableChartIcon from '@mui/icons-material/TableChart';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import HighlightIcon from '@mui/icons-material/Highlight';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import TitleIcon from '@mui/icons-material/Title';
import type { Editor } from '@tiptap/core';
import { SEARCE_COLORS } from '../../theme';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  minHeight?: number;
}

const TEXT_COLORS = [
  '#000000',
  '#333333',
  '#666666',
  '#0064FF',
  '#734BFF',
  '#d32f2f',
  '#388e3c',
  '#f57c00',
];

const HIGHLIGHT_COLORS = [
  '#ffff00',
  '#90caf9',
  '#a5d6a7',
  '#ffcc80',
  '#ef9a9a',
  '#ce93d8',
  '#b0bec5',
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = '',
  error = false,
  helperText,
  minHeight = 120,
}) => {
  const lastValueRef = useRef<string>(value);
  const [colorMenuAnchor, setColorMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [highlightMenuAnchor, setHighlightMenuAnchor] = React.useState<null | HTMLElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TableKit.configure({
        table: {
          resizable: true,
          HTMLAttributes: { class: 'rte-table' },
        },
      }),
      TextAlign.configure({
        types: ['paragraph', 'heading', 'blockquote'],
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'rte-content',
        'data-placeholder': placeholder,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastValueRef.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const currentHtml = editor.getHTML();
    if (value !== lastValueRef.current && value !== currentHtml) {
      lastValueRef.current = value;
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          border: `1px solid ${error ? SEARCE_COLORS.error : '#d0d7de'}`,
          borderRadius: '4px',
          overflow: 'hidden',
          '& .rte-content': {
            minHeight: `${minHeight}px`,
            padding: '16px 20px',
            fontSize: '14px',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            outline: 'none',
          },
          '& .ProseMirror p': {
            margin: '0 0 0.75em 0',
            paddingLeft: 0,
          },
          '& .ProseMirror ul': {
            margin: '0.5em 0',
            paddingLeft: '24px',
          },
          '& .ProseMirror ol': {
            margin: '0.5em 0',
            paddingLeft: '24px',
          },
          '& .ProseMirror blockquote': {
            margin: '0.75em 0',
            paddingLeft: '16px',
            borderLeft: `4px solid ${SEARCE_COLORS.primary}`,
          },
          '& .rte-content[data-placeholder]:empty::before': {
            content: 'attr(data-placeholder)',
            color: '#9ca3af',
          },
          '& .tiptap p.is-editor-empty:first-child::before': {
            color: '#9ca3af',
            content: 'attr(data-placeholder)',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
          '& .ProseMirror .tableWrapper': {
            margin: '12px 0',
            overflowX: 'auto',
          },
          '& .ProseMirror table': {
            borderCollapse: 'collapse',
            width: '100%',
            tableLayout: 'fixed',
            overflow: 'hidden',
          },
          '& .ProseMirror th, & .ProseMirror td': {
            border: '1px solid #d0d7de',
            padding: '8px 12px',
            boxSizing: 'border-box',
            minWidth: 24,
            position: 'relative',
            verticalAlign: 'top',
          },
          '& .ProseMirror th': {
            backgroundColor: '#f8fafc',
            fontWeight: 600,
          },
          '& .ProseMirror .selectedCell::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            background: 'rgba(0, 100, 255, 0.12)',
            pointerEvents: 'none',
            zIndex: 2,
          },
          '& .ProseMirror .column-resize-handle': {
            position: 'absolute',
            right: -2,
            top: 0,
            bottom: -1,
            width: 4,
            backgroundColor: SEARCE_COLORS.primary,
            pointerEvents: 'none',
          },
          '& .ProseMirror.resize-cursor': {
            cursor: 'col-resize',
          },
        }}
      >
        {/* Main toolbar */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            p: 0.5,
            borderBottom: `1px solid ${error ? SEARCE_COLORS.error : '#d0d7de'}`,
            backgroundColor: '#f8fafc',
          }}
        >
          <ToggleButtonGroup size="small" sx={{ flexWrap: 'wrap' }}>
            <ToggleButton
              value="bold"
              selected={editor.isActive('bold')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="italic"
              selected={editor.isActive('italic')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="underline"
              selected={editor.isActive('underline')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="blockquote"
              selected={editor.isActive('blockquote')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
            >
              <FormatQuoteIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="bulletList"
              selected={editor.isActive('bulletList')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <FormatListBulletedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="orderedList"
              selected={editor.isActive('orderedList')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <FormatListNumberedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ width: 4 }} />
          <ToggleButtonGroup size="small">
            <ToggleButton
              value="left"
              selected={editor.isActive({ textAlign: 'left' })}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
            >
              <FormatAlignLeftIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="center"
              selected={editor.isActive({ textAlign: 'center' })}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
            >
              <FormatAlignCenterIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="right"
              selected={editor.isActive({ textAlign: 'right' })}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
            >
              <FormatAlignRightIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="justify"
              selected={editor.isActive({ textAlign: 'justify' })}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            >
              <FormatAlignJustifyIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Text color">
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => setColorMenuAnchor(e.currentTarget)}
            >
              <FormatColorTextIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={colorMenuAnchor}
            open={Boolean(colorMenuAnchor)}
            onClose={() => setColorMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            {TEXT_COLORS.map((c) => (
              <MenuItem
                key={c}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  editor.chain().focus().setColor(c).run();
                  setColorMenuAnchor(null);
                }}
              >
                <Box sx={{ width: 20, height: 20, bgcolor: c, border: '1px solid #ccc', mr: 1 }} />
                {c}
              </MenuItem>
            ))}
          </Menu>
          <Tooltip title="Highlight">
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => setHighlightMenuAnchor(e.currentTarget)}
            >
              <HighlightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={highlightMenuAnchor}
            open={Boolean(highlightMenuAnchor)}
            onClose={() => setHighlightMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <MenuItem
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                editor.chain().focus().unsetHighlight().run();
                setHighlightMenuAnchor(null);
              }}
            >
              Remove highlight
            </MenuItem>
            {HIGHLIGHT_COLORS.map((c) => (
              <MenuItem
                key={c}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  editor.chain().focus().setHighlight({ color: c }).run();
                  setHighlightMenuAnchor(null);
                }}
              >
                <Box sx={{ width: 20, height: 20, bgcolor: c, border: '1px solid #ccc', mr: 1 }} />
              </MenuItem>
            ))}
          </Menu>

          <Box sx={{ width: 4 }} />
          <Tooltip title="Insert table">
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                editor.chain().focus().run();
                editor.commands.insertTable({ rows: 3, cols: 3, withHeaderRow: true });
              }}
            >
              <TableChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            title="Undo"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().undo().run()}
          >
            <UndoIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title="Redo"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().redo().run()}
          >
            <RedoIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Table bubble menu: show when cursor is inside a table */}
        <BubbleMenu
          editor={editor}
          updateDelay={0}
          shouldShow={({ editor: ed }: { editor: Editor }) =>
            ed.isFocused &&
            (ed.isActive('table') ||
              ed.isActive('tableCell') ||
              ed.isActive('tableHeader') ||
              ed.isActive('tableRow'))
          }
          options={{
            placement: 'top',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              p: 0.5,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              boxShadow: 2,
            }}
          >
            <Tooltip title="Add column before">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().addColumnBefore().run()}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add column after">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().addColumnAfter().run()}
              >
                <AddIcon fontSize="small" style={{ transform: 'rotate(-90deg)' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete column">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().deleteColumn().run()}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add row before">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().addRowBefore().run()}
              >
                <AddIcon fontSize="small" style={{ transform: 'rotate(-90deg)' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add row after">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().addRowAfter().run()}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete row">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().deleteRow().run()}
              >
                <DeleteOutlineIcon fontSize="small" style={{ transform: 'rotate(-90deg)' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Toggle header row">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().toggleHeaderRow().run()}
              >
                <TitleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Toggle header column">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
              >
                <TitleIcon fontSize="small" style={{ transform: 'rotate(-90deg)' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Merge cells">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().mergeCells().run()}
              >
                <MergeTypeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Split cell">
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().splitCell().run()}
              >
                <CallSplitIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete table">
              <IconButton
                size="small"
                color="error"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().deleteTable().run()}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </BubbleMenu>

        <EditorContent editor={editor} />
      </Box>
      {helperText && (
        <FormHelperText error={error} sx={{ mt: 1, ml: 1.5 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default RichTextEditor;
